import React from 'react';
import { SlideDeckSummary } from '../../../../services/api.service';
import { formatSecondsShort } from '../../utils/audio-segments';
import { BADGE_COLORS, BADGE_LABELS, SlideBadgeType } from '../../utils/slide-panel.utils';
import styles from './video-analytics.module.scss';

type Props = {
  durationSec: number;
  slideDeckSummary: SlideDeckSummary | null;
  eventCounts: Record<SlideBadgeType, number>;
  framesAnalyzed?: number;
  slidesCount?: number;
  onSummarizeSlides?: () => Promise<void>;
  onGeneratePptx?: () => Promise<void>;
  summarizing?: boolean;
  generatingPptx?: boolean;
};

const EVENT_ORDER: SlideBadgeType[] = ['decision', 'kpi', 'conflict', 'risk', 'task'];

const InsightsSidebar: React.FC<Props> = ({
  durationSec,
  slideDeckSummary,
  eventCounts,
  framesAnalyzed,
  slidesCount,
  onSummarizeSlides,
  onGeneratePptx,
  summarizing,
  generatingPptx,
}) => {
  const overview = slideDeckSummary?.overview?.trim();
  const topics = slideDeckSummary?.key_topics?.slice(0, 5) ?? [];
  const hasEvents = EVENT_ORDER.some(k => eventCounts[k] > 0);

  return (
    <aside className={styles.insightsSidebar}>
      <div className={styles.insightsSidebarBlock}>
        <div className={styles.insightsSidebarLabel}>Длительность</div>
        <div className={styles.insightsSidebarValue}>{formatSecondsShort(durationSec)}</div>
      </div>

      {(framesAnalyzed != null || slidesCount != null) && (
        <div className={styles.insightsSidebarBlock}>
          <div className={styles.insightsSidebarLabel}>VLM-анализ</div>
          <div className={styles.insightsSidebarMeta}>
            {framesAnalyzed != null && <span>{framesAnalyzed} кадров</span>}
            {slidesCount != null && <span>{slidesCount} слайдов</span>}
          </div>
        </div>
      )}

      {hasEvents && (
        <div className={styles.insightsSidebarBlock}>
          <div className={styles.insightsSidebarLabel}>События на встрече</div>
          <div className={styles.insightsSidebarEvents}>
            {EVENT_ORDER.filter(k => eventCounts[k] > 0).map(key => (
              <div key={key} className={styles.insightsSidebarEvent}>
                <span
                  className={styles.insightsSidebarEventDot}
                  style={{ backgroundColor: BADGE_COLORS[key] }}
                />
                <span className={styles.insightsSidebarEventLabel}>{BADGE_LABELS[key]}</span>
                <span className={styles.insightsSidebarEventCount}>{eventCounts[key]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(overview || topics.length > 0) && (
        <div className={styles.insightsSidebarBlock}>
          <div className={styles.insightsSidebarLabelRow}>
            <span className={styles.insightsSidebarLabel}>ИИ-резюме</span>
            <span className={styles.insightsSidebarAiBadge}>Qwen</span>
          </div>
          {overview && <p className={styles.insightsSidebarText}>{overview}</p>}
          {topics.length > 0 && (
            <ul className={styles.insightsSidebarTopics}>
              {topics.map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {(onSummarizeSlides || onGeneratePptx) && (
        <div className={styles.insightsSidebarActions}>
          {onSummarizeSlides && (
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => void onSummarizeSlides()}
              disabled={summarizing}
            >
              {summarizing ? 'Суммаризация…' : 'Обновить суммаризацию'}
            </button>
          )}
          {onGeneratePptx && (
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => void onGeneratePptx()}
              disabled={generatingPptx}
            >
              {generatingPptx ? 'Генерация…' : 'Сгенерировать pptx'}
            </button>
          )}
        </div>
      )}
    </aside>
  );
};

export default InsightsSidebar;
